import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import BrandLogo from './BrandLogo'
import AudiencePill from './ds/AudiencePill'
import Button from './ds/Button'
import { dashboardUrl } from '../lib/dashboardUrl'
import { trackEvent } from '../lib/analytics'

// The site header: wordmark, the audience pill, the trunk’s own links, one action.
//
// Monzo’s shape exactly, and for Monzo’s reason: a visitor should be able to tell
// which half of the marketplace they are reading in one glance, and switch in one
// click. The pill does that, so the links beside it only ever belong to the trunk
// you are in (docs/MARKETING-SITE-SPEC.md §3).
//
// Transparent over the hero, solid once scrolled. The hero is dark in both trunks,
// so the transparent state is always the light-on-dark one.

// ⛔ THE TRUNK LINKS LIVE IN THE PILL'S DROPDOWN, not in the bar.
// That is Monzo's own arrangement and the reason their header is so quiet:
// wordmark, pill, sign in, one action, and nothing else. Duplicating the three
// section links alongside the pill that already lists them crowds the bar and
// gives the same destination two places to drift. The mobile sheet keeps them
// flat, because a phone has no hover and a nested menu there is a worse answer.
const WORK_LINKS = [
  { to: '/how-it-works', label: 'How it works' },
  { to: '/getting-paid', label: 'Getting paid' },
  { to: '/trust',        label: 'Trust' },
]

const COVER_LINKS = [
  { to: '/business/how-it-works', label: 'How it works' },
  { to: '/business/pricing',      label: 'Pricing' },
  { to: '/business/compliance',   label: 'Compliance' },
]

export default function MarketingNav() {
  const { pathname } = useLocation()
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  const sheetRef = useRef(null)

  const business = pathname.startsWith('/business')
  const links    = business ? COVER_LINKS : WORK_LINKS

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Close on route change, close on Escape, lock the page behind the sheet.
  useEffect(() => { setOpen(false) }, [pathname])

  // ⚠️ THE SHEET MUST TRAP FOCUS, and the scroll lock is why.
  //
  // Opening it set body overflow hidden but confined nothing, so tabbing past
  // the last link walked into the page BEHIND the sheet. Because the scroll lock
  // caps scrollY at ~41px, the browser then could not bring the focused element
  // into view: from the 10th stop onward focus was on something at y=2045 and
  // y=4901, off screen, with no way to see where it was. Focus simply vanished.
  //
  // Escape already closed the sheet and restored focus to the burger; that part
  // was right and is kept.
  useEffect(() => {
    if (!open) return
    const sheet = sheetRef.current
    const opener = document.activeElement

    const focusables = () => Array.from(
      sheet?.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? [],
    ).filter((el) => el.offsetParent !== null)

    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); return }
      if (e.key !== 'Tab') return
      const items = focusables()
      if (!items.length) return
      const first = items[0]
      const last = items[items.length - 1]
      // Wrap at both ends, and pull focus back in if it has already escaped.
      if (e.shiftKey && (document.activeElement === first || !sheet?.contains(document.activeElement))) {
        e.preventDefault(); last.focus()
      } else if (!e.shiftKey && (document.activeElement === last || !sheet?.contains(document.activeElement))) {
        e.preventDefault(); first.focus()
      }
    }

    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    focusables()[0]?.focus()
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
      if (opener instanceof HTMLElement) opener.focus()
    }
  }, [open])

  const solid = scrolled || open
  const onStart = () => trackEvent('begin_registration', { source: 'nav', trunk: business ? 'cover' : 'work' })

  return (
    <>
      {/* The skip link. Eight tab stops of chrome precede page content on
          desktop, and a keyboard user should not have to walk them on every
          page. Visually hidden until focused, then it appears over the nav. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[200] focus:rounded-[14px] focus:bg-ink focus:px-4 focus:py-2.5 focus:text-[15px] focus:font-semibold focus:text-white"
      >
        Skip to content
      </a>
      <nav
        aria-label="Primary"
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          solid ? 'bg-canvas/95 backdrop-blur-md border-b border-hairline' : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-4 px-6">
          <Link to={business ? '/business' : '/'} aria-label="TempDash home">
            <BrandLogo size="lg" variant="light" />
          </Link>

          {/* ⛔ WRAPPED, not `hidden sm:inline-flex` on the pill itself.
              AudiencePill’s own base class is `inline-flex`, and two display
              utilities on one element resolve by stylesheet order rather than
              class order, so the pill stayed visible at 390px and pushed the
              nav off the screen. A wrapper has no competing display class. */}
          <div className="hidden sm:block">
            <AudiencePill />
          </div>

          <div className="ml-auto flex items-center gap-3">
            <a
              href={dashboardUrl('/login')}
              className="hidden text-[15px] text-ink-muted transition-colors hover:text-ink sm:inline"
            >
              Sign in
            </a>
            {/* Same wrapper, same reason: Button’s base class is inline-flex. */}
            <div className="hidden md:block">
              <Button
                href={business ? dashboardUrl('/register') : '/signup?for=work'}
                onClick={onStart}
                size="sm"
              >
                {business ? 'Post a shift' : 'Join TempDash'}
              </Button>
            </div>

            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              aria-controls="mobile-menu"
              className="inline-flex h-10 w-10 items-center justify-center rounded-[12px] border border-hairline bg-surface md:hidden"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                {open ? <path d="M18 6 6 18M6 6l12 12" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {open && (
        <div
          ref={sheetRef}
          id="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          className="fixed inset-x-0 top-16 z-40 border-b border-hairline bg-canvas px-6 pb-8 pt-6 md:hidden"
        >
          <div className="sm:hidden">
            <AudiencePill />
          </div>
          <ul className="mt-6 space-y-1">
            {links.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="block py-3 text-[19px] font-semibold text-ink">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-6 flex flex-col gap-3">
            <Button href={business ? dashboardUrl('/register') : '/signup?for=work'} onClick={onStart}>
              {business ? 'Post a shift' : 'Join TempDash'}
            </Button>
            <Button href={dashboardUrl('/login')} variant="outline">Sign in</Button>
          </div>
        </div>
      )}
    </>
  )
}
